import { TableFilter } from "@neonumy/ant-design";
import { clientEntities } from "@neonumy/api-clients";
import { Space, Tooltip } from "antd";
import Text from "antd/es/typography/Text";
import SelectAll from "components/SelectAll/SelectAll";
import { DATE_FORMAT, DATE_FORMAT_API } from "config/settings";
import dayjs from "dayjs";
import { LIST_CLIENTS_PAGINATED_SELECT } from "queries/entities/clients";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { DebitItemsSelection } from "./types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useCoreStore from "views/hooks/useCoreStore";
import useDirectDebitApi from "views/Payments/hooks/useDirectDebitApi";
import useFlexTableHeight from "./hooks/useFlexTableHeight";

interface InvoicesToBeDrawnTablePropsTypes {
  deadlineDate?: dayjs.Dayjs;
  selectedRows: DebitItemsSelection;
  setSelectedRows: Dispatch<SetStateAction<DebitItemsSelection>>;
}

export default function InvoicesToBeDrawnTable(
  props: InvoicesToBeDrawnTablePropsTypes,
): React.ReactElement {
  const { deadlineDate, setSelectedRows, selectedRows } = props;
  const { companyId }: RouteParams = useParams();
  const { t } = useTranslation();

  const currency = useCoreStore(state => state.currency);
  const decimalPlaces = currency?.decimalPlaces;

  const [currentPage, setCurrentPage] = useState(1);
  /* the modal is tall enough to fill a much larger page than the table default */
  const [pageSize, setPageSize] = useState(50);
  const { selectedRowKeys } = selectedRows;

  /* Reserve room for the sticky header, pagination and the select-all bar. */
  const [tableSlotRef, tableHeight] = useFlexTableHeight(Boolean(deadlineDate), 110);

  const {
    listDirectDebitSaleDocuments,
    getDirectDebitSaleDocumentsIds,
    loadingListDirectDebitInvoices: loading,
    refetchListDirectDebitInvoices: refetch,
    listInvoicesData: dataSource,
    ddSaleDocsCount: totalCount,
  } = useDirectDebitApi();

  useEffect(() => {
    if (deadlineDate) {
      setCurrentPage(1);
      listDirectDebitSaleDocuments({
        variables: {
          filter: {
            companyId: companyId,
            date: dayjs(deadlineDate).format(DATE_FORMAT_API),
            isValid: true,
          },
          limit: pageSize,
          offset: 0,
        },
      });
    }
  }, [deadlineDate]);

  const COLUMNS = [
    {
      title: t("Date"),
      dataIndex: "date",
      key: "date",
      width: 110,
      filterType: {
        type: "DateFilter",
        maxDate: dayjs().endOf("day"),
        varName: ["date_Gte", "date_Lte"],
      },
      render: (value: string) => {
        if (value) {
          return (
            <Space size="small" direction="vertical">
              <Text>{dayjs(value).format(DATE_FORMAT)}</Text>
            </Space>
          );
        }
      },
    },
    {
      title: t("Deadline"),
      dataIndex: "paymentDeadlineDate",
      key: "paymentDeadlineDate",
      width: 110,
      render: (value: string) => {
        if (value) {
          return (
            <Space size="small" direction="vertical">
              <Text>{dayjs(value).format(DATE_FORMAT)}</Text>
            </Space>
          );
        }
      },
    },
    {
      key: "reference",
      title: t("Reference"),
      dataIndex: "reference",
      width: 130,
    },
    {
      title: t("Client"),
      dataIndex: "client",
      key: "client",
      width: 170,
      sorter: true,
      filterType: {
        type: "AsyncSelectFilter",
        varName: "group_ClientId",
        asyncSelectSettings: {
          translations: {
            label: t("Select client"),
            placeholder: t("Select an option"),
            loadingMessage: t("Loading"),
            noOptionsMessage: t("No options"),
            button: {
              search: t("Search"),
              reset: t("Reset"),
            },
          },
          hasQueryFilter: true,
          queryOptions: {
            client: clientEntities,
            dataName: "listEntities",
            query: LIST_CLIENTS_PAGINATED_SELECT,
            variables: {
              filter: {
                companyId,
                entityType: "CLIENT",
                search: "",
              },
              limit: 20,
              orderBy: ["name"],
            },
          },
        },
      },
      render: (_text: unknown, record: ListDirectDebitInvoicesData) => {
        return <span>{`${record?.client?.name || ""}`}</span>;
      },
    },
    {
      key: "name",
      title: t("Name"),
      dataIndex: "name",
      width: 240,
      ellipsis: { showTitle: false },
      render: (value: string) => (
        <Tooltip title={value || ""} placement="topLeft">
          <span>{value}</span>
        </Tooltip>
      ),
    },
    {
      title: t("Sepa order"),
      key: "sepa",
      dataIndex: "sepa",
      width: 150,
      render: (_text: unknown, record: ListDirectDebitInvoicesData) => {
        return <span>{`${record?.sepa?.paymentPeriod?.label || ""}`}</span>;
      },
    },

    {
      key: "totalPrice",
      title: t("Total TTC"),
      dataIndex: "totalPrice",
      align: "right",
      width: 120,
      className: "debits-modal__num",
      render: (_text: unknown, record: ListDirectDebitInvoicesData) => {
        return <span>{`${Number(record?.totalPrice).toFixed(decimalPlaces)}€`}</span>;
      },
    },

    {
      key: "debt",
      title: t("Amount to be deducted"),
      dataIndex: "debt",
      align: "right",
      width: 130,
      className: "debits-modal__num",
      render: (_text: unknown, record: ListDirectDebitInvoicesData) => {
        return <span>{`${Number(record?.debt).toFixed(decimalPlaces) || 0.0}€`}</span>;
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    columnWidth: "4%" as const,
    onChange: (selectedRowKeys: React.Key[], selectedRecords: ListDirectDebitInvoicesData[]) => {
      setSelectedRows({
        ...selectedRows,
        selectedRowKeys,
        selectedItems: selectedRecords.map((record: ListDirectDebitInvoicesData) => ({
          id: record?.id,
          groupId: record?.group?.id,
        })),
      });
    },
  };

  const handlePagination = (page: number, size: number) => {
    setPageSize(size);
    setCurrentPage(page);
    refetch({
      filter: {
        companyId,
        date: dayjs(deadlineDate).format(DATE_FORMAT_API),
        isValid: true,
      },
      limit: size,
      offset: size * (page - 1),
    });
  };

  const handleOnClickSelectAll = async () => {
    const response = await getDirectDebitSaleDocumentsIds({
      variables: {
        filter: {
          companyId,
          date: dayjs(deadlineDate).format(DATE_FORMAT_API),
          isValid: true,
        },
      },
    });

    const results = response?.data?.listDirectDebitSaleDocuments?.results;
    const allRowsKeys =
      results?.map(item => item?.id).filter((id): id is NonNullable<ID> => id !== undefined) ?? [];
    const allItems =
      results?.map(item => ({
        id: item?.id,
        groupId: item?.group?.id,
      })) ?? [];

    setSelectedRows({
      selectedRowKeys: allRowsKeys,
      selectedItems: allItems,
    });
  };

  const handleOnClickReset = () => {
    setSelectedRows({
      selectedRowKeys: [],
      selectedItems: [],
    });
  };

  return (
    <>
      {deadlineDate && (
        <div className="debits-modal__table-slot" ref={tableSlotRef}>
          <SelectAll
            handleOnClickSelectAll={handleOnClickSelectAll}
            handleOnClickReset={handleOnClickReset}
            totalRowsSelected={Number(selectedRowKeys?.length)}
            totalRows={Number(totalCount)}
          />
          <TableFilter
            className="antd-newrai-table debits-modal__table"
            size="small"
            loading={loading}
            /*
             * `y` must be a pixel number — antd inlines it as the body's
             * max-height, so a percentage would collapse the table. It is
             * measured from the flex slot above.
             *
             * No `x`: a fixed value smaller than the container makes antd
             * stretch the header to the container while pinning the body
             * table to that value, so the two <colgroup>s disagree and the
             * trailing columns render past the visible edge. Letting the
             * columns fill naturally keeps header and body in lockstep.
             */
            scroll={{ y: tableHeight }}
            rowSelection={deadlineDate && rowSelection}
            columns={COLUMNS}
            dataSource={dataSource}
            pagination={{
              onChange: handlePagination,
              showSizeChanger: true,
              current: currentPage,
              pageSize: pageSize,
              total: totalCount,
              showTotal: total => t("Total table items", { count: total }),
            }}
          />
        </div>
      )}
    </>
  );
}
